import { useQuery } from "@apollo/client";
import classNames from "classnames";
import { isNumber } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import ProductItem from "~/components/Items/ProductItem";
import FeaturedProductsLoading from "~/components/Skeletons/FeaturedProductsLoading";
import Text from "~/components/Text";
import { QUERY_GET_COLLECTIONS_BLOCKS } from "~/data/collections";
import { Product, ProductCategory } from "~/services/product";
import { trackingViewListItem } from "~/services/tracking";
import { transformProductItems } from "~/utils/transform";
import styles from "./FeaturedProducts.module.scss";
import useClient from "~/hooks/useClient";
import { GET_PRODUCTS_TRACKING } from "~/data/product/gql";

type Props = {
  // identifier of collections
  identifier: string;
  activeTab: string;
  loading?: boolean;
  dataCollectionSsr?: any;
};

const FeatureProductItem = ({
  identifier,
  activeTab,
  dataCollectionSsr,
}: Props) => {
  const isClient = useClient();
  const [swiperRef, setSwiperRef] = useState<SwiperClass>({} as SwiperClass);
  const _slidesPerView =
    (swiperRef?.params?.slidesPerView as number) || (3 as number);
  const [isIntersecting, setIntersecting] = useState(false);
  const [trackedSKUs, setTrackedSKUs] = useState(new Set());

  const {
    data: dataCollection,
    loading: loadingCollection,
    fetchMore,
  } = useQuery(QUERY_GET_COLLECTIONS_BLOCKS, {
    variables: {
      url_key: identifier,
      page_size: 3,
      current_page: 1,
    },
    context: { fetchOptions: { method: "GET" } },
    skip: !identifier || activeTab !== identifier,
  });

  const customList = useMemo(() => {
    return (
      transformProductItems(
        (isClient ? dataCollection : dataCollectionSsr)?.productCollection?.[0]
          ?.items,
      ) || []
    );
  }, [dataCollection, isClient]);

  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus: customList.map((product) => product.sku + ""),
      },
      context: { fetchOptions: { method: "GET" } },
      skip: !customList.length,
    });

  const _list = useMemo(() => {
    if (loadingCollectionTracking || !dataCollectionTracking) return customList;
    return customList.map((product: Product) => {
      const tracking = dataCollectionTracking?.productsBySkus?.items?.find(
        (item: any) => item.sku === product.sku,
      );
      return {
        ...product,
        ...tracking,
      };
    });
  }, [loadingCollectionTracking, dataCollectionTracking, customList]);

  const page_info = useMemo(() => {
    return {
      total_count: dataCollection?.productCollection?.[0]?.total_count,
      current_page: dataCollection?.productCollection?.[0]?.current_page,
    };
  }, [dataCollection?.productCollection?.[0]]);

  const handleFecthMore = useCallback(() => {
    const current = page_info.current_page + 1;
    if (current <= page_info?.total_count) {
      fetchMore({
        variables: {
          current_page: current,
        },
        updateQuery(previousQueryResult, options) {
          const { fetchMoreResult } = options;
          if (!fetchMoreResult) return previousQueryResult;
          const result = Object.assign({}, previousQueryResult, {
            productCollection: [
              {
                ...fetchMoreResult.productCollection[0],
                items: [
                  ...previousQueryResult.productCollection[0].items,
                  ...fetchMoreResult.productCollection[0].items,
                ],
              },
            ],
          });
          return result;
        },
      });
    }
  }, [page_info]);

  const handleRealIndexChange = useCallback(
    (swiper: SwiperClass) => {
      if (!hasNext) return;
      if (!!loadingCollection) return;
      // if (products.length > MAX_RECORD) return;
      const currentIndex = swiper?.realIndex ?? 0;
      const silderPerView =
        typeof swiper?.params?.slidesPerView === "number"
          ? swiper?.params?.slidesPerView
          : 3;

      const remainingProducts = _list.length - currentIndex;

      if (remainingProducts <= silderPerView) {
        return handleFecthMore?.();
      }
    },
    [_list],
  );

  const hasNext = page_info?.total_count > page_info?.current_page;

  // check invisible product-list

  // Send tracked SKUs to Google Analytics
  const sendImpressions = useCallback(
    (itemInView: string[]) => {
      const skusToTrack: string[] = Array.from(itemInView).filter(
        (sku) => !trackedSKUs.has(sku),
      );

      if (skusToTrack.length > 0) {
        trackingViewListItem({
          listKey: identifier,
          products: _list.filter((product) =>
            skusToTrack.includes(product.sku + ""),
          ),
        });
        setTrackedSKUs((prev) => {
          const updatedSKUs = new Set(prev); // Create a new Set based on previous state
          skusToTrack.forEach((sku) => updatedSKUs.add(sku)); // Add new SKUs
          return updatedSKUs; // Return new Set
        });
      }
    },
    [_list, trackedSKUs],
  );

  const observerRef = useRef<any>(null);
  useEffect(() => {
    if (activeTab === identifier) {
      observerRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          setIntersecting(entry.isIntersecting);
        });
      });
      const target = document.getElementById(identifier);
      observerRef.current.observe(target);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [activeTab]);

  useEffect(() => {
    if (isIntersecting && !swiperRef?.destroyed) {
      const activeIndex = swiperRef.activeIndex;
      const silderPerView = swiperRef.params?.slidesPerView;
      if (activeIndex >= 0 && isNumber(silderPerView)) {
        const itemInView = _list?.slice(
          activeIndex,
          activeIndex + silderPerView,
        );
        sendImpressions(itemInView?.map((item) => item.sku + "") || []);
      }
    }
  }, [isIntersecting, swiperRef, _list.length]);

  return (
    <div
      id={identifier}
      className={classNames(
        "min-h-[200px] duration-300  opacity-0 invisible z-0 hidden overflow-hidden",
        {
          "!opacity-100 !visible  !block": activeTab === identifier,
        },
      )}
    >
      {!!loadingCollection && !_list.length && (
        <Swiper
          slidesPerView={1}
          breakpoints={{
            768: {
              slidesPerView: 2,
              slidesPerGroup: 2,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 3,
              slidesPerGroup: 3,
              spaceBetween: 10,
            },
          }}
        >
          {Array.from({ length: 3 }, (item, index: number) => {
            return (
              <SwiperSlide className={"border"} key={index}>
                <div className={styles.product_item}>
                  <FeaturedProductsLoading />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
      {!!_list.length && (
        <Swiper
          onSwiper={setSwiperRef}
          // onAfterInit={handleAfterInit}
          slidesPerView={1}
          // loop={true}
          navigation={{
            enabled: true,
          }}
          slidesPerGroup={1}
          breakpoints={{
            768: {
              slidesPerView: 2,
              slidesPerGroup: 2,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 3,
              slidesPerGroup: 3,
              spaceBetween: 10,
            },
          }}
          onRealIndexChange={handleRealIndexChange}
          modules={[Navigation]}
        >
          {_list?.map((data: Product, index: number) => {
            return (
              <SwiperSlide
                className="mb-0 wrap-product-item"
                key={index}
                data-sku={data.sku}
              >
                <ProductItem
                  data={data}
                  type="horizontal"
                  className={styles.product_item}
                />
              </SwiperSlide>
            );
          })}
          {hasNext &&
            Array.from({ length: _slidesPerView }, (item, index: number) => {
              return (
                <SwiperSlide className="px-2" key={index}>
                  <div className={classNames(styles.product_item)}>
                    <FeaturedProductsLoading />
                  </div>
                </SwiperSlide>
              );
            })}
        </Swiper>
      )}
      {!loadingCollection && !_list.length && (
        <div className="flex items-center justify-center h-[200px] w-full ">
          <Text type="title-3">Not Found Product</Text>
        </div>
      )}
    </div>
  );
};

export default FeatureProductItem;
export const getMaxLevelCategory = (categories: any[]): ProductCategory => {
  let maxLevel = 0;
  const maxLevelCategory: ProductCategory = categories?.reduce(
    (acc: ProductCategory = {} as ProductCategory, item: any) => {
      if (item.level > maxLevel) {
        maxLevel = item.level;
        acc = item;
      }
      return acc;
    },
    {} as ProductCategory,
  );

  return maxLevelCategory;
};
